import {
  ObjectPlatform,
  ObjectCollection,
  Renderer,
  GroupedObjectInfo,
  GroupByPropertyDisplay,
  ResolvedElementPropertyRule,
  ObjectSelectorPattern,
} from '@fullstory/renderer-shared';
import { ElementPropertyRule } from '@fullstory/proto/fs/services/search-settings';
import { NativePlatform, PlatformSelector } from '@fullstory/proto/fs/api/segment';
import { assertExhaustive } from '@fullstory/typeutils';
import {
  FsPropertiesSchemaAttributes,
  transformedValue,
  compileRegexValueFormat,
  rawValueAsDate,
} from '@fullstory/named-elements-ui';
import * as vars from '@fullstory/proto/fs/api/vars';
import { FieldScope, parseUnscopedFieldName } from '@fullstory/search-vars';
import {
  ToSelectorParts,
  SelectorMatcher,
  SelectorChainRule,
  makeOrderedSelectorRules,
  parseSelector,
} from '@fullstory/selectors';

export interface PropertyValuesConfig {
  propertyType: vars.Type;
  computedPropertyValues: ComputedPropertyValues;
}

export type ComputedPropertyValues = Map<string, ComputedPropertyValue>;

export interface ComputedPropertyValue {
  normalizedValue: string;
  depth: number;
}

export function computePropertyValuesConfig(
  focusPaths: Set<string> | undefined,
  groupByProperty: GroupByPropertyDisplay | undefined,
  renderer: Renderer,
): PropertyValuesConfig | undefined {
  if (!groupByProperty) {
    return undefined;
  }

  const computedPropertyValues = new Map<string, ComputedPropertyValue>();
  const { varType } = parseUnscopedFieldName(groupByProperty.propertyName, FieldScope.Element);
  const propertyType = vars.Type[varType];

  // There are two ways properties can be set on elements -- we need to respect each.
  // First is using the elements api in code with the 'data-fs-properties-schema' attribute.
  // Second is using in app configured property rules.
  const { propertyName, propertyRules } = groupByProperty;

  for (const rule of propertyRules) {
    addPropertyValuesForRule(rule, focusPaths, computedPropertyValues, renderer);
  }

  // process api values second to prefer when overwriting elements that may have already been processed
  const propertiesSchemaCollection = renderer.queryObjects({
    patterns: FsPropertiesSchemaAttributes.map(key => ({
      type: 'selector',
      selector: `[${key}]`,
    })),
  });
  for (const [path, object] of Object.entries(propertiesSchemaCollection)) {
    if (!satisfiesFocus(focusPaths, path, true, renderer)) {
      continue;
    }
    const depth = computeDepth(object);
    const propertyValues = apiPropertyValuesForObject(new Set([propertyName]), object);
    for (const value of propertyValues) {
      const normalizedValue = normalizePropertyValue(value.value, propertyType);
      if (normalizedValue !== undefined) {
        computedPropertyValues.set(path, { normalizedValue, depth });
      }
    }
  }

  return { propertyType, computedPropertyValues };
}

type ComputedChildPropertyValue = {
  value: string | undefined;
  parentDepth: number;
  childDepth: number;
};

export function addPropertyValuesForRule(
  rule: ResolvedElementPropertyRule,
  focusPaths: Set<string> | undefined,
  computedPropertyValues: Map<string, ComputedPropertyValue>,
  renderer: Renderer,
) {
  const childPatterns = childPatternsForRule(rule);
  const patterns = parentPatternsForRule(rule, childPatterns !== undefined);
  const producer = propertyValueProducerForRule(rule);
  if (!patterns.length || (childPatterns !== undefined && !childPatterns.length) || !producer) {
    // some configuration couldn't be resolved, skip
    return;
  }

  const maybeAddComputedPropertyValue = (
    path: string,
    value: string | undefined,
    depth: number,
  ) => {
    // check the normalized value before adding to ensure that we will end with a defined value
    // if undefined, we will skip and allow other rules to take precedence
    const normalizedValue = normalizePropertyValue(value, rule.propertyType);
    if (normalizedValue !== undefined) {
      computedPropertyValues.set(path, { normalizedValue, depth });
    }
  };
  const computedChildValues = new Map<string, ComputedChildPropertyValue>();

  const objectCollection = leastDepthObjects(renderer.queryObjects({ patterns }), patterns);
  const childObjectCollection =
    childPatterns !== undefined
      ? leastDepthObjects(renderer.queryObjects({ patterns: childPatterns }), childPatterns)
      : undefined;
  const hasChild = !!childObjectCollection;

  // values come from only the child (implied when there is no parent <-> child relationship)
  const valueCollection = childObjectCollection ?? objectCollection;
  // and parent serialized selectors is only present when there is an explicit parent <-> child relationship
  const parents = hasChild
    ? Object.entries(objectCollection).map(([path, object]) => ({
        path,
        object,
        depth: computeDepth(object),
      }))
    : [];

  for (const [path, object] of Object.entries(valueCollection)) {
    // need to enforce focus first
    if (!satisfiesFocus(focusPaths, path, true, renderer)) {
      continue;
    }
    // then we can do the rest and set the resulting value if applicable
    const rawPropertyValue = rawPropertyValueForObject(rule, object);
    const value = producer(rawPropertyValue);
    const valueDepth = computeDepth(object);
    if (hasChild) {
      // we need to set a computed property value for each parent of this child
      parents.forEach(parent => {
        const existing = computedChildValues.get(parent.path);
        if (!existing || valueDepth < existing.childDepth) {
          // only handle if there is no existing / or we could actually improve the depth
          // but still need to check descendancy
          if (renderer.isDescendant(path, parent.path)) {
            // the current value object is actually parented by this item
            // need to set the path and depth based on the parent
            computedChildValues.set(parent.path, {
              value,
              parentDepth: parent.depth,
              childDepth: valueDepth,
            });
          }
        }
      });
    } else {
      // we can set the value directly
      maybeAddComputedPropertyValue(path, value, valueDepth);
    }
  }

  // and only once we are done enumerating all children can we commit those values when applicable
  computedChildValues.forEach((value, parentPath) =>
    maybeAddComputedPropertyValue(parentPath, value.value, value.parentDepth),
  );
}

function leastDepthObjects(collection: ObjectCollection, patterns: Pattern[]): ObjectCollection {
  const result: ObjectCollection = {};
  const selectorMatcher = new SelectorMatcher();
  for (const [path, object] of Object.entries(collection)) {
    const objectSelectorParts = ToSelectorParts(object.selector);
    const matches = patterns.some(pattern => {
      if (!pattern.leastDepthSelector) {
        // be default, this one matches, it does not enforce least depth
        return true;
      }
      const fullDepthMatch = true;
      const result = selectorMatcher.matchesWithDepths(
        pattern.leastDepthSelector,
        objectSelectorParts,
        fullDepthMatch,
      );
      return result.matches;
    });
    if (matches) {
      result[path] = object;
    }
  }
  return result;
}

function computeDepth(object: GroupedObjectInfo): number {
  let depth = 0;
  for (let cur = object.selector.parent; cur; cur = cur.parent) {
    depth++;
  }
  return depth;
}

type Pattern = ObjectSelectorPattern & {
  leastDepthSelector: SelectorChainRule[] | undefined;
};

function parentPatternsForRule(rule: ResolvedElementPropertyRule, hasChild: boolean): Pattern[] {
  const element = ElementPropertyRule.element_oneof(rule);
  if (!element) return [];
  switch (element.opt) {
    case ElementPropertyRule.element.any: {
      // right now, we only support attribute extraction for 'any'
      // if set, we produce an attribute exists selector
      const attr: string = rule.attribute?.attribute;
      return attr
        ? [
            {
              type: 'selector',
              selector: `[${attr}]`,
              leastDepthSelector: undefined,
            },
          ]
        : [];
    }
    case ElementPropertyRule.element.selectors:
      return (
        element.val?.selectors?.map(ps => ({
          type: 'selector',
          ...objectSelector(ps),
          opts: {
            includeInnerText: !!rule.innerText && !hasChild,
          },
        })) ?? []
      );
    case ElementPropertyRule.element.namedElementId:
      // should not be possible given we are starting with a resolved element property rule
      // still including for exhaustiveness
      return [];
    default:
      assertExhaustive(element);
  }
}

function childPatternsForRule(rule: ResolvedElementPropertyRule): Pattern[] | undefined {
  const child = ElementPropertyRule.child_oneof(rule);
  if (!child) return undefined;
  switch (child.opt) {
    case ElementPropertyRule.child.anyChild: {
      // right now, we only support attribute extraction for 'anyChild'
      // if set, we produce an attribute exists selector
      const attr: string = rule.attribute?.attribute;
      return attr
        ? [
            {
              type: 'selector',
              selector: `[${attr}]`,
              leastDepthSelector: undefined,
            },
          ]
        : [];
    }
    case ElementPropertyRule.child.childSelectors:
      return (
        child.val?.selectors?.map(ps => ({
          type: 'selector',
          ...objectSelector(ps),
          opts: {
            includeInnerText: !!rule.innerText,
          },
        })) ?? []
      );
    case ElementPropertyRule.child.childNamedElementId:
      // should not be possible given we are starting with a resolved element property rule
      // still including for exhaustiveness
      return [];
    default:
      assertExhaustive(child);
  }
}

function objectSelector(ps: PlatformSelector): {
  selector: string;
  leastDepthSelector: SelectorChainRule[];
  platforms?: ObjectPlatform[];
} {
  let leastDepthSelector: SelectorChainRule[] = [];
  try {
    const selectorChain = parseSelector(ps.selector);
    leastDepthSelector = makeOrderedSelectorRules(selectorChain);
  } catch (e) {
    // just let the least depth selector be empty, this selector shouldn't match anything
  }

  if (!ps.platforms.length) {
    // all platforms
    return { selector: ps.selector, leastDepthSelector };
  }

  return {
    selector: ps.selector,
    leastDepthSelector,
    platforms: ps.platforms
      .map((p): ObjectPlatform | null => {
        switch (p) {
          case NativePlatform.NATIVE_PLATFORM_INVALID:
            return null;
          case NativePlatform.NATIVE_PLATFORM_WEB:
            return 'web';
          case NativePlatform.NATIVE_PLATFORM_ANDROID:
            return 'android';
          case NativePlatform.NATIVE_PLATFORM_IOS:
            return 'ios';
          default:
            assertExhaustive(p);
        }
      })
      .filter((p): p is ObjectPlatform => !!p),
  };
}

export function rawPropertyValueForObject(
  rule: ResolvedElementPropertyRule,
  object: GroupedObjectInfo,
): string | undefined {
  const source = ElementPropertyRule.source_oneof(rule);
  if (!source) return undefined;
  switch (source.opt) {
    case ElementPropertyRule.source.attribute:
      // take the attribute from the depth most selector part, we do not work up the chain
      // we do need to specially handle id and class as they have their own first class fields
      switch (source.val.attribute) {
        case 'id':
          return object.selector.rule?.id || undefined;
        case 'class':
          return object.selector.rule?.classes?.join(' ') || undefined;
        default:
          return object.selector.rule?.attrs?.[source.val.attribute] || undefined;
      }
    case ElementPropertyRule.source.innerText:
      // for text, the object holds the computed text
      return object.innerText;
    case ElementPropertyRule.source.static:
      // static just comes straight from the rule config
      return source.val.value;
    default:
      assertExhaustive(source);
  }
}

function propertyValueProducerForRule(
  rule: ResolvedElementPropertyRule,
): ((raw: string | undefined) => string | undefined) | undefined {
  const producer = ElementPropertyRule.value_producer_oneof(rule);
  if (!producer) return raw => raw;
  if (producer.opt === ElementPropertyRule.value_producer.regex) {
    const { format, error } = compileRegexValueFormat(producer.val.format);
    if (error) {
      return undefined;
    }
    try {
      const expression = new RegExp(producer.val.expression);
      return raw => transformedValue(expression, format, raw);
    } catch (e) {
      return undefined;
    }
  }
  return undefined;
}

export function apiPropertyValuesForObject(
  propertyNames: Set<string>,
  object: GroupedObjectInfo,
): { propertyName: string; value: string | undefined }[] {
  let rawSchema: string | undefined;
  for (const key of FsPropertiesSchemaAttributes) {
    rawSchema = object.selector.rule?.attrs?.[key];
    if (rawSchema) {
      break;
    }
  }
  if (!rawSchema || !propertyNames.size) return [];

  const values: { propertyName: string; value: string | undefined }[] = [];
  try {
    const schema = JSON.parse(rawSchema);
    const attrs = Object.keys(schema);
    for (const attr of attrs) {
      const config = schema[attr];
      let name: string = attr;
      let type: string | undefined;
      if (typeof config === 'string') {
        // config indicates the type directly
        type = config;
      } else {
        // config should be an object with a type key and optional name override
        Object.keys(config).forEach(key => {
          const configValue = config[key];
          if (typeof configValue === 'string') {
            switch (key) {
              case 'type':
                type = configValue;
                break;
              case 'name':
                name = configValue;
                break;
            }
          }
        });
      }
      if (type !== undefined) {
        const computedPropertyName = `${name}_${type}`;
        if (propertyNames.has(computedPropertyName)) {
          values.push({
            propertyName: computedPropertyName,
            value: object.selector.rule?.attrs?.[attr] || undefined,
          });
        }
      }
    }
  } catch (e) {
    // just fallthrough and return empty values / values up to this point
  }

  return values;
}

export function computeFocusPaths(
  selectors: string[],
  renderer: Renderer,
): Set<string> | undefined {
  if (!selectors.length) {
    return undefined;
  }
  const collection = renderer.queryObjects({
    patterns: selectors.map(selector => ({ type: 'selector', selector })),
  });
  return new Set<string>(Object.keys(collection));
}

export function satisfiesConstraints(
  rawPropertyValue: string | undefined,
  path: string,
  focusPaths: Set<string> | undefined,
  propertyValuesConfig: PropertyValuesConfig | undefined,
  renderer: Renderer,
): boolean {
  // fast path, no constraints
  if (!focusPaths && !propertyValuesConfig) {
    return true;
  }
  // if we don't have a valid path, we can't satisfy the constraints
  if (!path) {
    return false;
  }
  if (focusPaths && !satisfiesFocus(focusPaths, path, false, renderer)) {
    return false;
  }
  if (rawPropertyValue !== undefined && propertyValuesConfig) {
    const collection = renderer.queryObjects({
      patterns: [
        {
          type: 'path',
          path,
        },
      ],
    });
    // really should only be one match, but will enumerate anyways
    for (const path of Object.keys(collection)) {
      if (!satisfiesPropertyValue(rawPropertyValue, path, propertyValuesConfig, renderer)) {
        return false;
      }
    }
  }
  return true;
}

export function satisfiesFocus(
  focusPaths: Set<string> | undefined,
  path: string,
  checkAncestor: boolean,
  renderer: Renderer,
): boolean {
  if (!focusPaths || focusPaths.has(path)) {
    return true;
  }
  for (const focusPath of focusPaths) {
    if (
      renderer.isDescendant(path, focusPath) ||
      (checkAncestor && renderer.isDescendant(focusPath, path))
    ) {
      return true;
    }
  }
  return false;
}

export function satisfiesPropertyValue(
  rawPropertyValue: string,
  path: string,
  propertyValuesConfig: PropertyValuesConfig,
  renderer: Renderer,
): boolean {
  const propertyValue = normalizePropertyValue(rawPropertyValue, propertyValuesConfig.propertyType);
  const computedPropertyValue = getComputedNormalizedPropertyValue(
    path,
    propertyValuesConfig.computedPropertyValues,
    renderer,
  );
  return computedPropertyValue !== undefined && propertyValue === computedPropertyValue;
}

export function getComputedNormalizedPropertyValue(
  path: string,
  computedPropertyValues: ComputedPropertyValues,
  renderer: Renderer,
): string | undefined {
  // exact match is the fast path
  // we know this is going to be the best option as it is inherently the deepest computed value
  const value = computedPropertyValues.get(path);
  if (value) {
    return value.normalizedValue;
  }
  // otherwise, we need to scan our computed properties and find the deepest match relative to the provided element
  let bestValue: ComputedPropertyValue | undefined = undefined;
  for (const [valuePath, value] of computedPropertyValues.entries()) {
    if ((!bestValue || value.depth > bestValue.depth) && renderer.isDescendant(path, valuePath)) {
      bestValue = value;
    }
  }
  return bestValue?.normalizedValue;
}

const boolFalse = new Set(['0', 'f', 'false']);

export function normalizePropertyValue(
  rawPropertyValue: string | undefined,
  propertyType: vars.Type,
): string | undefined {
  switch (propertyType) {
    case vars.Type.bool: {
      // raw value can be undefined which implies false, otherwise if it is set and non false, it is true
      const value =
        rawPropertyValue === undefined ? false : !boolFalse.has(rawPropertyValue.toLowerCase());
      return value ? 'true' : 'false';
    }
    case vars.Type.date: {
      const date: Date = rawValueAsDate(rawPropertyValue);
      return date ? date.toISOString() : undefined;
    }
    case vars.Type.int: {
      const int = Number(rawPropertyValue);
      return Number.isInteger(int) ? int.toString(10) : undefined;
    }
    case vars.Type.real: {
      const real = Number(rawPropertyValue);
      return Number.isFinite(real) ? real.toString(10) : undefined;
    }
    case vars.Type.str: {
      return rawPropertyValue;
    }
    case vars.Type.INVALID_TYPE:
    // fallthrough, invalid
    case vars.Type.bools:
    // fallthrough, not supported
    case vars.Type.dates:
    // fallthrough, not supported
    case vars.Type.ints:
    // fallthrough, not supported
    case vars.Type.reals:
    // fallthrough, not supported
    case vars.Type.strs:
      // not supported
      return undefined;
    default:
      assertExhaustive(propertyType);
  }
}
